import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Code, Calendar, ShoppingCart, Zap, Database, Palette } from 'lucide-react';

const iconMap = {
  1: Code,
  2: Calendar,
  3: ShoppingCart,
  4: Zap,
  5: Database,
  6: Palette
};

const CapabilityCard = ({ capability, index }) => {
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef(null);
  const Icon = iconMap[capability.id];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        // THE FIX: This now toggles automatically! True when visible, false when hidden.
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <Card
      ref={cardRef}
      className={`group bg-zinc-900 hover:shadow-2xl hover:shadow-black/50 transition-all duration-1000 ease-out border-zinc-800 hover:border-[#4a8282]/50 cursor-pointer hover:-translate-y-2 hover:scale-105 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-16'
        }`}
      style={{
        transitionDelay: `${(index % 3) * 300}ms`,
        transformStyle: 'preserve-3d'
      }}
    >
      <CardHeader>
        {capability.video ? (
          <div className="mb-4 rounded-lg overflow-hidden bg-zinc-950">
            <video
              src={capability.video}
              autoPlay
              loop
              muted
              playsInline
              className="w-full h-48 object-cover opacity-90 group-hover:opacity-100 transition-opacity"
            />
          </div>
        ) : (
          <div className="w-14 h-14 bg-zinc-950 border border-zinc-800 rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#4a8282] transition-colors duration-300">
            <Icon className="w-7 h-7 text-[#4a8282] group-hover:text-white transition-colors duration-300" />
          </div>
        )}
        <CardTitle className="text-2xl mb-2 text-white group-hover:text-[#4a8282] transition-colors duration-300">
          {capability.title}
        </CardTitle>
        <CardDescription className="text-base leading-relaxed text-zinc-400">
          {capability.description}
        </CardDescription>
      </CardHeader>
      <CardContent></CardContent>
    </Card>
  );
};

const CapabilitiesSection = ({ capabilities }) => {
  const [isHeaderVisible, setIsHeaderVisible] = useState(false);
  const headerRef = useRef(null);

  useEffect(() => {
    const headerObserver = new IntersectionObserver(
      ([entry]) => {
        // THE FIX: Header also toggles on and off now
        setIsHeaderVisible(entry.isIntersecting);
      },
      { threshold: 0.1 }
    );

    if (headerRef.current) {
      headerObserver.observe(headerRef.current);
    }

    return () => headerObserver.disconnect();
  }, []);

  return (
    <section id="capabilities" className="py-24 lg:py-32 bg-zinc-950 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">

        {/* Section Header */}
        <div
          ref={headerRef}
          className={`text-center mb-16 transition-all duration-1000 ease-out ${isHeaderVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
            }`}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            What <span className="text-[#4a8282]">Gets Built</span>
          </h2>
          <p className="text-xl text-zinc-400 max-w-2xl mx-auto">
            End-to-end solutions tailored to your business needs
          </p>
        </div>

        {/* Capabilities Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {capabilities.map((capability, index) => (
            <CapabilityCard key={capability.id} capability={capability} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default CapabilitiesSection;